import { Injectable } from "@angular/core";
import { OAuthService } from "angular-oauth2-oidc";
import { authConfig } from "../config/auth.config";
import { ReplaySubject } from "rxjs/internal/ReplaySubject";
import { UserModel } from "../models/user";
import { Observable } from "rxjs";
import { Router } from "@angular/router";
import { HttpHeaders } from '@angular/common/http';

@Injectable({
  providedIn: "root",
})
export class AuthService {
    private _user: ReplaySubject<UserModel> = new ReplaySubject<UserModel>(1);

    get user$(): Observable<UserModel> {
        return this._user.asObservable();
      }
    
    constructor(private readonly oauthService: OAuthService, private _router: Router) {
        this.configure();
    }

    private configure() {
        this.oauthService.configure(authConfig);
        this.oauthService.loadDiscoveryDocumentAndLogin().then(isAuthenticated => {
            if (!isAuthenticated) {
              this.oauthService.initLoginFlow(window.location.pathname);
            } else {
              this.loadUser();
            }
          });
        this.oauthService.setupAutomaticSilentRefresh();
      }

    public loadUser() {
        this.oauthService.loadUserProfile().then((user: any) => {
            this.mapUser(user);
            const returnUrl = decodeURIComponent(this.oauthService.state || '');
            if (returnUrl) {
                this._router.navigateByUrl(returnUrl);
            }
        });
    }

    mapUser(user: any) {
        const usuarioModel: UserModel = {
          id: user['info']['sub'],
          email: user['info']['email'],
          name: user['info']['given_name'],
          lastName: user['info']['family_name'],
          username: user['info']['preferred_username'],
          fullName: user['info']['name'],
        }

        this._user.next(usuarioModel);
    }

    /**
     * Get user roles from JWT token
     * Decodes the access token and extracts the roles array
     */
    public getRoles(): string[] {
        try {
            const token = this.oauthService.getAccessToken();
            if (!token) return [];

            // Decode JWT token (split and parse the payload)
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.roles || [];
        } catch (error) {
            return [];
        }
    }

    /**
     * Extract resource name from role
     * Role format: {permission}_{resource}
     * Example: read_chatbot -> chatbot
     */
    private extractResourceFromRole(role: string): string {
        const parts = role.split('_');
        return parts.length > 1 ? parts[1] : '';
    }

    /**
     * Check if user has access to a specific resource
     * Matches route ID with resource name from roles
     * Only removes hyphens for comparison (e.g., "user-management" matches "usermanagement")
     */
    public hasAccessToResource(resourceId: string): boolean {
        const roles = this.getRoles();
        const resources = roles.map(role => this.extractResourceFromRole(role));
        
        // Normalize: only remove hyphens for comparison
        const normalizedId = resourceId.toLowerCase().replace(/-/g, '').split('/')[0];
        
        // Check if any resource matches exactly after removing hyphens
        return resources.some(resource => {
            const normalizedResource = resource.toLowerCase().replace(/-/g, '');
            return normalizedResource === normalizedId;
        });
    }

    /**
     * Check if user has any of the required permissions with implication logic
     * 
     * Permission levels and HTTP operations:
     * - read_X: Only GET (view/list)
     * - run_X: GET + some POST (view/list + execute/use)
     * - write_X: Full CRUD (GET, POST, PUT, DELETE)
     * 
     * Implication chain:
     * - write_X implies run_X and read_X (can do everything)
     * - run_X implies read_X (can read + execute)
     * - read_X is base level (can only read)
     * 
     * @param requiredPermissions Array of required permissions (e.g., ['read_chatbot', 'run_chatbot'])
     * @returns true if user has at least one of the required permissions (or implied permissions)
     */
    public hasAnyPermission(requiredPermissions: string[]): boolean {
        const userRoles = this.getRoles();
        
        // Check each required permission
        return requiredPermissions.some(required => {
            // Direct match
            if (userRoles.includes(required)) {
                return true;
            }
            
            // Extract permission level and resource
            const parts = required.split('_');
            if (parts.length < 2) return false;
            
            const level = parts[0]; // read, run, or write
            const resource = parts.slice(1).join('_'); // resource name (e.g., 'chatbot', 'system_params')
            
            // Check implications based on permission hierarchy
            if (level === 'read') {
                // read_X can be satisfied by run_X or write_X
                const runRole = `run_${resource}`;
                const writeRole = `write_${resource}`;
                return userRoles.includes(runRole) || userRoles.includes(writeRole);
            } else if (level === 'run') {
                // run_X can be satisfied by write_X
                const writeRole = `write_${resource}`;
                return userRoles.includes(writeRole);
            }
            
            // For write_X, only exact match (already checked above)
            return false;
        });
    }

    /**
   * Get authorization headers with OAuth token
   */
    getAuthHeaders(): HttpHeaders {
        const token = this.oauthService?.getAccessToken();
        return new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
        });
    }

    /**
     * Logout user and redirect to login
     */
    logout(): void {
        this.oauthService.logOut();
    }
}
